import { useState, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/contexts/AuthContext";
import { saveFootprintsAsFinal } from "../../../../services/footprintsService";
import {
  CheckCircle,
  ArrowUpToLine,
  AlertTriangle,
  FileText,
  Zap,
  CalendarIcon,
  XCircle,
  AlertCircle,
  Droplets,
  Package,
  BarChart4,
  Loader2,
  Settings,
  Check,
  RefreshCw,
} from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useLanguage } from "@/contexts/LanguageContext";
import { ConfidentialityDisclaimer } from "@/components/footprints/ConfidentialityDisclaimer";
import { formatNumber } from "@/utils/numberFormatter";
import { validateProductTotals } from "@/components/footprints/utils/calculations/extraction";

interface RevisionSubmissionStepProps {
  formData: Record<string, any>;
  year: string;
  onSubmissionSuccess?: () => void;
  company?: any;
  onSaveDraft?: () => void;
  isSavingDraft?: boolean;
  hasUnsavedChanges?: boolean;
  lastSaved?: Date | null;
  originalStatus?: "draft" | "submitted" | null;
  onNavigateToStep?: (stepIndex: number, subStep?: number) => void;
}

interface StepAnalysis {
  name: string;
  icon: React.ReactNode;
  isComplete: boolean;
  hasData: boolean;
  warnings: string[];
  summary: {
    entries?: number;
    carbonTotal?: number;
    hydricTotal?: number;
    details?: Record<string, any>;
  };
}

export const RevisionSubmissionStep = ({
  formData,
  year,
  onSubmissionSuccess,
  company,
  onSaveDraft,
  isSavingDraft,
  hasUnsavedChanges,
  lastSaved,
  originalStatus,
  onNavigateToStep,
}: RevisionSubmissionStepProps) => {
  const { claims } = useAuth();
  const { t } = useLanguage();
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Recalculate product validation to ensure it's up-to-date with current general data
  const recalculatedProducts = useMemo(() => {
    if (!formData.products?.entries?.length || !formData.generalData || !company?.activityType) {
      return formData.products;
    }

    const isMixedCompany = company.activityType === "extraction_and_transformation";
    const isTransformationCompany = company.activityType === "transformation";
    const isExtractionCompany = company.activityType === "extraction";

    let isProductionValid = false;
    let isRevenueValid = false;

    if (isMixedCompany) {
      // For mixed companies, validate each activity type separately
      const extractionProducts = formData.products.entries.filter((p: any) => p.productFlow === "extraction");
      const transformationProducts = formData.products.entries.filter((p: any) => p.productFlow === "transformation");

      // Calculate extraction totals
      const extractionValidation = validateProductTotals(extractionProducts, {
        prod_ext: formData.generalData.prod_ext || 0,
        fat_ext: formData.generalData.fat_ext || 0,
      });

      // Calculate transformation totals
      const transformationValidation = validateProductTotals(transformationProducts, {
        prod_ext: formData.generalData.prod_trsf || 0,
        fat_ext: formData.generalData.fat_trsf || 0,
      });

      isProductionValid = extractionValidation.isProductionValid && transformationValidation.isProductionValid;
      isRevenueValid = extractionValidation.isRevenueValid && transformationValidation.isRevenueValid;
    } else {
      // For single activity companies
      let validation;
      if (isTransformationCompany) {
        validation = validateProductTotals(formData.products.entries, {
          prod_ext: formData.generalData.prod_trsf || 0,
          fat_ext: formData.generalData.fat_trsf || 0,
        });
      } else if (isExtractionCompany) {
        validation = validateProductTotals(formData.products.entries, {
          prod_ext: formData.generalData.prod_ext || 0,
          fat_ext: formData.generalData.fat_ext || 0,
        });
      } else {
        // Fallback
        validation = validateProductTotals(formData.products.entries, {
          prod_ext: formData.generalData.prod_ext || 0,
          fat_ext: formData.generalData.fat_ext || 0,
        });
      }
      isProductionValid = validation.isProductionValid;
      isRevenueValid = validation.isRevenueValid;
    }

    return {
      ...formData.products,
      isProductionValid,
      isRevenueValid,
    };
  }, [
    formData.products,
    formData.generalData?.prod_ext,
    formData.generalData?.fat_ext,
    formData.generalData?.prod_trsf,
    formData.generalData?.fat_trsf,
    company?.activityType,
  ]);

  const handleSubmit = async () => {
    if (!claims?.companyId) {
      setSubmitError(t("companyDataNotFound"));
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      // Map annualConsumptions to backend structure before final submission
      let dataToSave = { ...formData };
      if (!dataToSave.generalData) dataToSave.generalData = {};
      
      // Try to get activityType from formData or fallback to claims/company context if available
      if (!dataToSave.generalData.activityType) {
        if (formData.generalData?.activityType) {
          dataToSave.generalData.activityType = formData.generalData.activityType;
        } else if (company?.activityType) {
          dataToSave.generalData.activityType = company.activityType;
        }
      }

      const activityType = dataToSave.generalData.activityType || company?.activityType;
      const isMixed = activityType === "extraction_and_transformation";

      if (isMixed && dataToSave.annualConsumptions) {
        // For mixed companies, process both extraction and transformation data
        const { extraction = {}, transformation = {} } = dataToSave.annualConsumptions;

        // Helper function to process material entries
        const processMaterialEntries = (entries: any[] = [], _activity: "extraction" | "transformation") => ({
          entries: entries.map((entry) => ({
            ...entry,
            materialName: entry.materialType,
            setup_item_name: entry.materialType,
            setup_item_id: entry.setup_item_id || null,
          })),
        });

        dataToSave.materials = {
          extraction: processMaterialEntries(extraction.materials?.entries, "extraction"),
          transformation: processMaterialEntries(transformation.materials?.entries, "transformation"),
        };
        dataToSave.electricity = {
          extraction: extraction.electricity || { entries: [] },
          transformation: transformation.electricity || { entries: [] },
        };
        dataToSave.fuels = {
          extraction: extraction.fuels || { entries: [] },
          transformation: transformation.fuels || { entries: [] },
        };
        dataToSave.residue = {
          extraction: extraction.residue || { entries: [] },
          transformation: transformation.residue || { entries: [] },
        };
        dataToSave.water = {
          extraction: extraction.water || { waterEntries: [] },
          transformation: transformation.water || { waterEntries: [] },
        };
      } else {
        // For single-activity companies, process data from the flat structure
        const activityKey = activityType === "extraction" ? "extraction" : "transformation";

        // Helper function to process material entries for single activity
        const processMaterialEntries = (entries: any[] = []) => ({
          entries: entries.map((entry) => ({
            ...entry,
            materialName: entry.materialType,
            setup_item_name: entry.materialType,
            setup_item_id: entry.setup_item_id || null,
            activity_type: activityKey,
          })),
        });

        // Process each category for single activity companies
        dataToSave.materials = {
          [activityKey]: processMaterialEntries(dataToSave.materials?.entries || []),
        };
        dataToSave.electricity = {
          [activityKey]: {
            entries: (dataToSave.electricity?.entries || []).map((e: any) => ({
              ...e,
              activity_type: activityKey,
            })),
          },
        };
        dataToSave.fuels = {
          [activityKey]: {
            entries: (dataToSave.fuels?.entries || []).map((e: any) => ({
              ...e,
              activity_type: activityKey,
            })),
          },
        };
        dataToSave.residue = {
          [activityKey]: {
            entries: (dataToSave.residue?.entries || []).map((e: any) => ({
              ...e,
              activity_type: activityKey,
            })),
          },
        };
        dataToSave.water = {
          [activityKey]: {
            waterEntries: (dataToSave.water?.waterEntries || []).map((e: any) => ({
              ...e,
              activity_type: activityKey,
            })),
          },
        };
      }

      if (formData.products?.deletedProductIds?.length > 0) {
        try {
          const { distributionService } = await import("@/services/distribution/distributionService");
          
          // Delete distribution data for each deleted product
          const deletePromises = formData.products.deletedProductIds.map((productId: string) => 
            distributionService.deleteDistributionByProduct(productId).catch(error => {
              console.warn(`Failed to delete distribution data for product ${productId}:`, error);
            })
          );
          
          await Promise.all(deletePromises);
          
          dataToSave.products = {
            ...dataToSave.products,
            deletedProductIds: []
          };
        } catch (error) {
          console.error("Error removing distribution data:", error);
        }
      }

      await saveFootprintsAsFinal(year, claims.companyId, dataToSave);
      setSubmitted(true);
      if (onSubmissionSuccess) {
        onSubmissionSuccess();
      }
    } catch (error: any) {
      console.error("Failed to save footprints data:", error);
      setSubmitError(error.message || t("error"));
    } finally {
      setIsSubmitting(false);
    }
  };

  // Helper function to get the correct data structure based on company type
  const getConsumptionData = (category: string) => {
    // Always use company activityType as the source of truth, not formData
    const activityType = company?.activityType || formData?.generalData?.activityType;

    console.log(`getConsumptionData - Category: ${category}, ActivityType: ${activityType}`, {
      formDataKeys: Object.keys(formData || {}),
      annualConsumptionsKeys: Object.keys(formData?.annualConsumptions || {}),
      extractionKeys: Object.keys(formData?.annualConsumptions?.extraction || {}),
      transformationKeys: Object.keys(formData?.annualConsumptions?.transformation || {}),
      flatCategoryData: formData?.[category],
      extractionCategoryData: formData?.annualConsumptions?.extraction?.[category],
      transformationCategoryData: formData?.annualConsumptions?.transformation?.[category],
    });

    if (activityType === "extraction_and_transformation") {
      // For mixed companies, check both extraction and transformation data
      const extractionData = formData?.annualConsumptions?.extraction?.[category];
      const transformationData = formData?.annualConsumptions?.transformation?.[category];

      console.log(`Mixed company - ${category}:`, {
        extractionData,
        transformationData,
        extractionEntries: extractionData?.entries?.length || 0,
        transformationEntries: transformationData?.entries?.length || 0,
        // Special handling for water
        extractionWaterEntries: extractionData?.waterEntries?.length || 0,
        transformationWaterEntries: transformationData?.waterEntries?.length || 0,
      });

      // Special handling for water category which has waterEntries instead of entries
      if (category === "water") {
        if (extractionData?.waterEntries?.length || transformationData?.waterEntries?.length) {
          return {
            waterEntries: [...(extractionData?.waterEntries || []), ...(transformationData?.waterEntries || [])],
            total_pc: (extractionData?.total_pc || 0) + (transformationData?.total_pc || 0),
            total_ph: (extractionData?.total_ph || 0) + (transformationData?.total_ph || 0),
            total_pc_mat: (extractionData?.total_pc_mat || 0) + (transformationData?.total_pc_mat || 0),
            total_ph_mat: (extractionData?.total_ph_mat || 0) + (transformationData?.total_ph_mat || 0),
            total_pc_ele: (extractionData?.total_pc_ele || 0) + (transformationData?.total_pc_ele || 0),
            total_ph_ele: (extractionData?.total_ph_ele || 0) + (transformationData?.total_ph_ele || 0),
            total_pc_comb: (extractionData?.total_pc_comb || 0) + (transformationData?.total_pc_comb || 0),
            total_ph_comb: (extractionData?.total_ph_comb || 0) + (transformationData?.total_ph_comb || 0),
            total_pc_res: (extractionData?.total_pc_res || 0) + (transformationData?.total_pc_res || 0),
            total_ph_agua_azul:
              (extractionData?.total_ph_agua_azul || 0) + (transformationData?.total_ph_agua_azul || 0),
            total_ph_agua_cinzenta:
              (extractionData?.total_ph_agua_cinzenta || 0) + (transformationData?.total_ph_agua_cinzenta || 0),
          };
        }
        return null;
      }

      // Return combined data or whichever has entries for other categories
      if (extractionData?.entries?.length || transformationData?.entries?.length) {
        return {
          entries: [...(extractionData?.entries || []), ...(transformationData?.entries || [])],
          total_pc: (extractionData?.total_pc || 0) + (transformationData?.total_pc || 0),
          total_ph: (extractionData?.total_ph || 0) + (transformationData?.total_ph || 0),
          total_pc_mat: (extractionData?.total_pc_mat || 0) + (transformationData?.total_pc_mat || 0),
          total_ph_mat: (extractionData?.total_ph_mat || 0) + (transformationData?.total_ph_mat || 0),
          total_pc_ele: (extractionData?.total_pc_ele || 0) + (transformationData?.total_pc_ele || 0),
          total_ph_ele: (extractionData?.total_ph_ele || 0) + (transformationData?.total_ph_ele || 0),
          total_pc_comb: (extractionData?.total_pc_comb || 0) + (transformationData?.total_pc_comb || 0),
          total_ph_comb: (extractionData?.total_ph_comb || 0) + (transformationData?.total_ph_comb || 0),
          total_pc_res: (extractionData?.total_pc_res || 0) + (transformationData?.total_pc_res || 0),
          total_ph_agua_azul: (extractionData?.total_ph_agua_azul || 0) + (transformationData?.total_ph_agua_azul || 0),
          total_ph_agua_cinzenta:
            (extractionData?.total_ph_agua_cinzenta || 0) + (transformationData?.total_ph_agua_cinzenta || 0),
        };
      }
      return null;
    } else {
      // For single activity companies, check multiple possible structures
      // First try the flat structure (legacy)
      let result = formData?.[category];
      
      // If not found, try the nested structure based on activity type
      if (!result && formData?.annualConsumptions) {
        if (activityType === "extraction") {
          result = formData.annualConsumptions.extraction?.[category];
        } else if (activityType === "transformation") {
          result = formData.annualConsumptions.transformation?.[category];
        }
      }
      
      // Fallback to the old structure
      if (!result) {
        result = formData?.annualConsumptions?.[category];
      }
      
      console.log(`Single activity company - ${category}:`, result);
      return result;
    }
  };

  // Simple step-by-step analysis
  const analyzeSteps = (): StepAnalysis[] => {
    const steps: StepAnalysis[] = [];
    // Always use company activityType as the source of truth, not formData
    const activityType = company?.activityType || formData?.generalData?.activityType;
    const isMixedCompany = activityType === "extraction_and_transformation";

    // 1. General Data Analysis
    const generalData = formData.generalData;
    steps.push({
      name: t("footprints.generalData.title"),
      icon: <Settings className="w-5 h-5 text-blue-500" />,
      isComplete: !!generalData,
      hasData: !!generalData,
      warnings: !generalData ? [t("footprints.generalData.activityTypeRequiredDescription")] : [],
      summary: {
        details: generalData
          ? {
              [t("footprints.revisionSubmission.production")]: isMixedCompany
                ? `${formatNumber(Number(generalData.prod_ext || 0) + Number(generalData.prod_trsf || 0))} t`
                : activityType === "transformation" 
                  ? `${formatNumber(Number(generalData.prod_trsf || 0))} t`
                  : `${formatNumber(Number(generalData.prod_ext || 0))} t`,
              [t("footprints.revisionSubmission.revenue")]: isMixedCompany
                ? `${formatNumber(Number(generalData.fat_ext || 0) + Number(generalData.fat_trsf || 0))} €`
                : activityType === "transformation"
                  ? `${formatNumber(Number(generalData.fat_trsf || 0))} €`
                  : `${formatNumber(Number(generalData.fat_ext || 0))} €`,
              "PVP Mais Caro": activityType === "transformation"
                ? `${formatNumber(generalData.pvp_caro_trsf || 0)} €/t`
                : `${formatNumber(generalData.pvp_barato_ext || 0)} €/t`,
              "PVP Mais Barato": activityType === "transformation"
                ? `${formatNumber(generalData.pvp_barato_trsf || 0)} €/t`
                : `${formatNumber(generalData.pvp_barato_ext || 0)} €/t`,
              [t("footprints.revisionSubmission.year")]: generalData.year || "N/A",
            }
          : {},
      },
    });

    // 2. Materials Analysis
    const materials = getConsumptionData("materials");
    const materialDetails: Record<string, any> = {};
    
    // For mixed companies, check both phases
    let materialsComplete = false;
    let materialsWarning = "";
    
    if (isMixedCompany) {
      const extractionMaterials = formData?.annualConsumptions?.extraction?.materials?.entries?.length || 0;
      const transformationMaterials = formData?.annualConsumptions?.transformation?.materials?.entries?.length || 0;
      
      materialsComplete = extractionMaterials > 0 && transformationMaterials > 0;
      
      if (extractionMaterials === 0 && transformationMaterials === 0) {
        materialsWarning = t("footprints.guidelines.materials");
      } else if (extractionMaterials === 0) {
        materialsWarning = t("footprints.guidelines.materialsExtractionRequired");
      } else if (transformationMaterials === 0) {
        materialsWarning = t("footprints.guidelines.materialsTransformationRequired");
      }
    } else {
      materialsComplete = !!materials?.entries?.length;
      materialsWarning = !materials?.entries?.length ? t("footprints.guidelines.materials") : "";
    }
    
    if (materials?.entries?.length) {
      materialDetails[t("footprints.revisionSubmission.materials")] = materials.entries.length;
      materials.entries.forEach((entry: any, index: number) => {
        const entryKey = `${t("footprints.revisionSubmission.materials")} ${index + 1}`;
        materialDetails[entryKey] = `${entry.materialType} - ${formatNumber(entry.quantity)} ${entry.unit}`;
        if (entry.pc_mat > 0) materialDetails[`${entryKey} (PC)`] = `${formatNumber(entry.pc_mat)} kg CO₂e`;
        if (entry.ph_mat > 0) materialDetails[`${entryKey} (PH)`] = `${formatNumber(entry.ph_mat)} m³`;
      });
    }

    steps.push({
      name: t("footprints.revisionSubmission.materials"),
      icon: <Package className="w-5 h-5 text-purple-500" />,
      isComplete: materialsComplete,
      hasData: !!materials?.entries?.length,
      warnings: materialsWarning ? [materialsWarning] : [],
      summary: {
        entries: materials?.entries?.length || 0,
        carbonTotal: materials?.total_pc_mat || 0,
        hydricTotal: materials?.total_ph_mat || 0,
        details: materialDetails,
      },
    });

    // 3. Electricity Analysis
    const electricity = getConsumptionData("electricity");
    const electricityDetails: Record<string, any> = {};
    
    // For mixed companies, check both phases
    let electricityComplete = false;
    let electricityWarning = "";
    
    if (isMixedCompany) {
      const extractionElectricity = formData?.annualConsumptions?.extraction?.electricity?.entries?.length || 0;
      const transformationElectricity = formData?.annualConsumptions?.transformation?.electricity?.entries?.length || 0;
      
      const extractionNoElec = formData?.annualConsumptions?.extraction?.electricity?.noElectricityConsumption || false;
      const transformationNoElec = formData?.annualConsumptions?.transformation?.electricity?.noElectricityConsumption || false;

      electricityComplete = (extractionElectricity > 0 || extractionNoElec) && (transformationElectricity > 0 || transformationNoElec);

      if (extractionElectricity === 0 && !extractionNoElec && transformationElectricity === 0 && !transformationNoElec) {
        electricityWarning = t("footprints.guidelines.electricity");
      } else if (extractionElectricity === 0 && !extractionNoElec) {
        electricityWarning = t("footprints.guidelines.electricityExtractionRequired");
      } else if (transformationElectricity === 0 && !transformationNoElec) {
        electricityWarning = t("footprints.guidelines.electricityTransformationRequired");
      }
    } else {
      const noElec = electricity?.noElectricityConsumption || false;
      electricityComplete = !!electricity?.entries?.length || noElec;
      electricityWarning = !electricity?.entries?.length && !noElec ? t("footprints.guidelines.electricity") : "";
    }
    
    if (electricity?.entries?.length) {
      electricityDetails[t("footprints.revisionSubmission.energy")] = electricity.entries.length;
      electricity.entries.forEach((entry: any, index: number) => {
        const entryKey = `${t("footprints.revisionSubmission.energy")} ${index + 1}`;
        electricityDetails[entryKey] = `${entry.energySource} - ${formatNumber(entry.quantity)} ${entry.unit}`;
        if (entry.pc_ele > 0) electricityDetails[`${entryKey} (PC)`] = `${formatNumber(entry.pc_ele)} kg CO₂e`;
        if (entry.ph_ele > 0) electricityDetails[`${entryKey} (PH)`] = `${formatNumber(entry.ph_ele)} m³`;
      });
    }

    steps.push({
      name: t("footprints.revisionSubmission.energy"),
      icon: <Zap className="w-5 h-5 text-yellow-500" />,
      isComplete: electricityComplete,
      hasData: !!electricity?.entries?.length,
      warnings: electricityWarning ? [electricityWarning] : [],
      summary: {
        entries: electricity?.entries?.length || 0,
        carbonTotal: electricity?.total_pc_ele || 0,
        hydricTotal: electricity?.total_ph_ele || 0,
        details: electricityDetails,
      },
    });

    // 4. Fuels Analysis
    const fuels = getConsumptionData("fuels");
    const fuelsDetails: Record<string, any> = {};
    
    // For mixed companies, check both phases
    let fuelsComplete = false;
    let fuelsWarning = "";
    
    if (isMixedCompany) {
      const extractionFuels = formData?.annualConsumptions?.extraction?.fuels?.entries?.length || 0;
      const transformationFuels = formData?.annualConsumptions?.transformation?.fuels?.entries?.length || 0;
      
      const extractionNoFuel = formData?.annualConsumptions?.extraction?.fuels?.noFuelConsumption || false;
      const transformationNoFuel = formData?.annualConsumptions?.transformation?.fuels?.noFuelConsumption || false;

      fuelsComplete = (extractionFuels > 0 || extractionNoFuel) && (transformationFuels > 0 || transformationNoFuel);

      if (extractionFuels === 0 && !extractionNoFuel && transformationFuels === 0 && !transformationNoFuel) {
        fuelsWarning = t("footprints.guidelines.fuels");
      } else if (extractionFuels === 0 && !extractionNoFuel) {
        fuelsWarning = t("footprints.guidelines.fuelsExtractionRequired");
      } else if (transformationFuels === 0 && !transformationNoFuel) {
        fuelsWarning = t("footprints.guidelines.fuelsTransformationRequired");
      }
    } else {
      const noFuel = fuels?.noFuelConsumption || false;
      fuelsComplete = !!fuels?.entries?.length || noFuel;
      fuelsWarning = !fuels?.entries?.length && !noFuel ? t("footprints.guidelines.fuels") : "";
    }
    
    if (fuels?.entries?.length) {
      fuelsDetails[t("footprints.revisionSubmission.fuels")] = fuels.entries.length;
      fuels.entries.forEach((entry: any, index: number) => {
        const entryKey = `${t("footprints.revisionSubmission.fuels")} ${index + 1}`;
        fuelsDetails[entryKey] = `${entry.fuelSource} - ${formatNumber(entry.quantity)} ${entry.unit}`;
        if (entry.pc_comb > 0) fuelsDetails[`${entryKey} (PC)`] = `${formatNumber(entry.pc_comb)} kg CO₂e`;
        if (entry.ph_comb > 0) fuelsDetails[`${entryKey} (PH)`] = `${formatNumber(entry.ph_comb)} m³`;
      });
    }

    steps.push({
      name: t("footprints.revisionSubmission.fuels"),
      icon: <FileText className="w-5 h-5 text-orange-500" />,
      isComplete: fuelsComplete,
      hasData: !!fuels?.entries?.length,
      warnings: fuelsWarning ? [fuelsWarning] : [],
      summary: {
        entries: fuels?.entries?.length || 0,
        carbonTotal: fuels?.total_pc_comb || 0,
        hydricTotal: fuels?.total_ph_comb || 0,
        details: fuelsDetails,
      },
    });

    // 5. Residue Analysis
    const residue = getConsumptionData("residue");
    const residueDetails: Record<string, any> = {};
    
    // For mixed companies, check both phases
    let residueComplete = false;
    let residueWarning = "";
    
    if (isMixedCompany) {
      const extractionResidue = formData?.annualConsumptions?.extraction?.residue?.entries?.length || 0;
      const transformationResidue = formData?.annualConsumptions?.transformation?.residue?.entries?.length || 0;
      
      residueComplete = extractionResidue > 0 && transformationResidue > 0;
      
      if (extractionResidue === 0 && transformationResidue === 0) {
        residueWarning = t("footprints.guidelines.residue");
      } else if (extractionResidue === 0) {
        residueWarning = t("footprints.guidelines.residueExtractionRequired");
      } else if (transformationResidue === 0) {
        residueWarning = t("footprints.guidelines.residueTransformationRequired");
      }
    } else {
      residueComplete = !!residue?.entries?.length;
      residueWarning = !residue?.entries?.length ? t("footprints.guidelines.residue") : "";
    }
    
    if (residue?.entries?.length) {
      residueDetails[t("footprints.revisionSubmission.residues")] = residue.entries.length;
      residue.entries.forEach((entry: any, index: number) => {
        const entryKey = `${t("footprints.revisionSubmission.residues")} ${index + 1}`;
        residueDetails[entryKey] = entry.residueType;
        if (entry.pc_res > 0) residueDetails[`${entryKey} (PC)`] = `${formatNumber(entry.pc_res)} kg CO₂e`;

        // Show treatments info
        if (entry.treatments?.length) {
          residueDetails[`${entryKey} - Tratamentos`] = entry.treatments.length;
          entry.treatments.forEach((treatment: any, treatmentIndex: number) => {
            const treatmentKey = `  Tratamento ${treatmentIndex + 1}`;
            residueDetails[treatmentKey] = `${formatNumber(treatment.quantity || 0)} (ID: ${
              treatment.treatmentTypeId
            })`;
          });
        }
      });
    }

    steps.push({
      name: t("footprints.revisionSubmission.residues"),
      icon: <AlertTriangle className="w-5 h-5 text-red-500" />,
      isComplete: residueComplete,
      hasData: !!residue?.entries?.length,
      warnings: residueWarning ? [residueWarning] : [],
      summary: {
        entries: residue?.entries?.length || 0,
        carbonTotal: residue?.total_pc_res || 0,
        details: residueDetails,
      },
    });

    // 6. Water Analysis
    const water = getConsumptionData("water");
    const waterDetails: Record<string, any> = {};
    
    // For mixed companies, check both phases
    let waterComplete = false;
    let waterWarning = "";
    
    if (isMixedCompany) {
      const extractionWater = formData?.annualConsumptions?.extraction?.water?.waterEntries?.length || 0;
      const transformationWater = formData?.annualConsumptions?.transformation?.water?.waterEntries?.length || 0;
      
      waterComplete = extractionWater > 0 && transformationWater > 0;
      
      if (extractionWater === 0 && transformationWater === 0) {
        waterWarning = t("footprints.guidelines.water");
      } else if (extractionWater === 0) {
        waterWarning = t("footprints.guidelines.waterExtractionRequired");
      } else if (transformationWater === 0) {
        waterWarning = t("footprints.guidelines.waterTransformationRequired");
      }
    } else {
      waterComplete = !!water?.waterEntries?.length;
      waterWarning = !water?.waterEntries?.length ? t("footprints.guidelines.water") : "";
    }
    
    if (water?.waterEntries?.length) {
      waterDetails[t("footprints.revisionSubmission.water")] = water.waterEntries.length;
      water.waterEntries.forEach((entry: any, index: number) => {
        const entryKey = `${t("footprints.revisionSubmission.water")} ${index + 1}`;
        waterDetails[`${entryKey} - Área`] = `${formatNumber(entry.waterSurfaceArea || 0)} m²`;

        if (entry.waterOrigins?.length) {
          waterDetails[`${entryKey} - Origens`] = entry.waterOrigins.length;
          entry.waterOrigins.forEach((origin: any, originIndex: number) => {
            waterDetails[`  Origem ${originIndex + 1}`] = `${formatNumber(origin.quantity)} m³`;
          });
        }

        if (entry.discharge?.hasDischarge && entry.discharge?.dischargeEntries?.length) {
          waterDetails[`${entryKey} - Descargas`] = entry.discharge.dischargeEntries.length;
        }

        if (entry.sludge?.hasSludge) {
          waterDetails[`${entryKey} - Lamas`] = `${formatNumber(entry.sludge.sludgeQuantity || 0)} ${
            entry.sludge.sludgeUnit || "kg"
          }`;
        }
      });
    }

    steps.push({
      name: t("footprints.revisionSubmission.water"),
      icon: <Droplets className="w-5 h-5 text-blue-400" />,
      isComplete: waterComplete,
      hasData: !!water?.waterEntries?.length,
      warnings: waterWarning ? [waterWarning] : [],
      summary: {
        entries: water?.waterEntries?.length || 0,
        hydricTotal: (water?.total_ph_agua_azul || 0) + (water?.total_ph_agua_cinzenta || 0),
        details: {
          ...waterDetails,
          "Água Azul Total": `${formatNumber(water?.total_ph_agua_azul || 0)} m³`,
          "Água Cinzenta Total": `${formatNumber(water?.total_ph_agua_cinzenta || 0)} m³`,
        },
      },
    });

    // 7. Products Analysis
    const products = recalculatedProducts;
    const productWarnings: string[] = [];
    const productDetails: Record<string, any> = {};

    // Check if there are relevant products for the company type
    let relevantProducts = products?.entries || [];
    if (activityType === "extraction") {
      relevantProducts = products?.entries?.filter((p: any) => !p.productFlow || p.productFlow === "extraction") || [];
    } else if (activityType === "transformation") {
      relevantProducts = products?.entries?.filter((p: any) => !p.productFlow || p.productFlow === "transformation") || [];
    }
    // For mixed companies, use all products
    
    if (relevantProducts.length === 0) {
      productWarnings.push(t("footprints.revisionSubmission.noProductsAdded"));
    }

    // Add validation error warnings - only for relevant products
    if (relevantProducts.length > 0) {
      if (!products.isProductionValid) {
        productWarnings.push(t("products.productionValidationError"));
      }
      if (!products.isRevenueValid) {
        productWarnings.push(t("products.revenueValidationError"));
      }
    }

    if (products?.entries) {
      productDetails[t("footprints.revisionSubmission.totalProducts")] = relevantProducts.length;
      relevantProducts.forEach((product: any, index: number) => {
        const productKey = `${t("footprints.revisionSubmission.product")} ${index + 1}`;
        productDetails[productKey] = product.name || t("common.notSpecified");
      });
    }

    steps.push({
      name: t("products.title"),
      icon: <CheckCircle className="w-5 h-5 text-green-500" />,
      isComplete: !!(relevantProducts.length > 0 && products.isProductionValid && products.isRevenueValid),
      hasData: relevantProducts.length > 0,
      warnings: productWarnings,
      summary: {
        entries: relevantProducts.length,
        details: {
          ...productDetails,
          "Validação Produção": products?.isProductionValid ? "✓ Válida" : "✗ Inválida",
          "Validação Faturação": products?.isRevenueValid ? "✓ Válida" : "✗ Inválida",
        },
      },
    });

    return steps;
  };

  const steps = analyzeSteps();
  const completedSteps = steps.filter((step) => step.isComplete).length;
  const stepsWithData = steps.filter((step) => step.hasData).length;
  const totalWarnings = steps.reduce((acc, step) => acc + step.warnings.length, 0);

  // Calculate totals
  const totalCarbonFootprint = steps.reduce((acc, step) => acc + (step.summary.carbonTotal || 0), 0);
  const totalHydricFootprint = steps.reduce((acc, step) => acc + (step.summary.hydricTotal || 0), 0);

  return (
    <div className="w-full max-w-5xl mx-auto">
      <ConfidentialityDisclaimer />
      <div className="bg-white rounded-xl shadow-md border border-gray-100">
        {/* Header */}
        <div className="px-6 pt-5 pb-4 border-b border-gray-100">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-teal-700">{t("footprints.revisionSubmission.title")}</h3>
              <p className="text-sm text-gray-500 mt-1">{t("footprints.revisionSubmission.description")}</p>
            </div>
            <div className="flex items-center text-teal-600 bg-teal-50 px-2.5 py-1 rounded-full border border-teal-200 text-xs font-medium">
              <CalendarIcon className="w-3.5 h-3.5 mr-1.5" />
              <span>{year}</span>
            </div>
          </div>
        </div>

        <div className="p-6">
          {/* Error List Display at Top - Always visible when there are warnings */}
          {totalWarnings > 0 && (
            <div className="space-y-4 mb-6">
              <div className="flex items-center justify-between">
                <h4 className="text-lg font-bold text-red-700 flex items-center gap-2">
                  <AlertTriangle className="w-6 h-6" />
                  {t("footprints.revisionSubmission.requiredActions")} ({totalWarnings})
                </h4>
              </div>

              <Alert className="border-red-300 bg-red-50 shadow-md">
                <AlertTriangle className="h-5 w-5 text-red-700" />
                <AlertDescription>
                  <p className="text-red-900 font-medium mb-1">
                    {t("footprints.revisionSubmission.cannotSubmit")}
                  </p>
                  <p className="text-red-800 text-sm">
                    {t("footprints.revisionSubmission.pleaseCompleteRequired")}
                  </p>
                </AlertDescription>
              </Alert>

              <Card className="border-red-300 bg-white shadow-md">
                <CardContent className="pt-6">
                  <div className="space-y-5">
                    {steps.map(
                      (step, stepIndex) =>
                        step.warnings.length > 0 && (
                          <div
                            key={stepIndex}
                            className="border-l-4 border-red-500 bg-red-50 rounded-r-lg p-4 hover:bg-red-100 transition-colors cursor-pointer"
                            onClick={() => {
                              if (onNavigateToStep) {
                                // Map step names to actual step indices
                                const stepMapping: Record<string, number> = {
                                  [t("footprints.generalData.title")]: 2,
                                  [t("footprints.revisionSubmission.materials")]: 1,
                                  [t("footprints.revisionSubmission.energy")]: 1,
                                  [t("footprints.revisionSubmission.fuels")]: 1,
                                  [t("footprints.revisionSubmission.residues")]: 1,
                                  [t("footprints.revisionSubmission.water")]: 1,
                                  [t("products.title")]: 3,
                                };

                                // Map Annual Consumptions items to their respective substep indices
                                const subStepMapping: Record<string, number> = {
                                  [t("footprints.revisionSubmission.materials")]: 0,
                                  [t("footprints.revisionSubmission.energy")]: 1,
                                  [t("footprints.revisionSubmission.fuels")]: 2,
                                  [t("footprints.revisionSubmission.residues")]: 3,
                                  [t("footprints.revisionSubmission.water")]: 4,
                                };

                                const targetStep = stepMapping[step.name];
                                const targetSubStep = subStepMapping[step.name];

                                if (targetStep !== undefined) {
                                  // If it's an Annual Consumptions step (step index 1) and has a substep mapping, pass both
                                  if (targetStep === 1 && targetSubStep !== undefined) {
                                    onNavigateToStep(targetStep, targetSubStep);
                                  } else {
                                    onNavigateToStep(targetStep);
                                  }
                                }
                              }
                            }}
                          >
                            <div className="flex items-start gap-3 mb-3">
                              <div className="mt-0.5">{step.icon}</div>
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-1">
                                  <h5 className="font-bold text-red-900 text-base">{step.name}</h5>
                                  <Badge variant="destructive" className="text-xs font-semibold">
                                    {step.warnings.length}
                                  </Badge>
                                </div>
                                <p className="text-xs text-red-700 italic">
                                  {t("footprints.revisionSubmission.clickToEdit")}
                                </p>
                              </div>
                            </div>
                            <ul className="space-y-3 ml-8">
                              {step.warnings.map((warning, warningIndex) => {
                                // Determine if warning is section-specific for mixed companies
                                const isMixedCompany = company?.activityType === "extraction_and_transformation";
                                let sectionBadge = null;

                                if (isMixedCompany) {
                                  const warningLower = warning.toLowerCase();
                                  if (warningLower.includes("extração") || warningLower.includes("extraction")) {
                                    sectionBadge = (
                                      <Badge className="bg-blue-500 text-white border-blue-600 text-xs font-semibold shadow-sm">
                                        <span className="mr-1">📍</span>
                                        {t("common.extraction")}
                                      </Badge>
                                    );
                                  } else if (warningLower.includes("transformação") || warningLower.includes("transformation")) {
                                    sectionBadge = (
                                      <Badge className="bg-purple-500 text-white border-purple-600 text-xs font-semibold shadow-sm">
                                        <span className="mr-1">📍</span>
                                        {t("common.transformation")}
                                      </Badge>
                                    );
                                  }
                                }

                                return (
                                  <li
                                    key={warningIndex}
                                    className="bg-white rounded-md p-3 border border-red-200 shadow-sm"
                                  >
                                    <div className="flex items-start gap-2">
                                      <AlertCircle className="w-5 h-5 mt-0.5 flex-shrink-0 text-red-600" />
                                      <div className="flex-1 space-y-2">
                                        <div className="flex flex-wrap items-center gap-2">
                                          {sectionBadge && <div>{sectionBadge}</div>}
                                          <span className="text-sm text-red-900 font-medium">{warning}</span>
                                        </div>
                                        <p className="text-xs text-gray-600 italic">
                                          {t("footprints.revisionSubmission.goBackToStep")}
                                        </p>
                                      </div>
                                    </div>
                                  </li>
                                );
                              })}
                            </ul>
                          </div>
                        )
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Overall Status */}
          <Card className="mb-6 bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200 shadow-sm">
            <CardContent className="pt-6">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 text-center">
                <div>
                  <div className="text-2xl font-bold text-blue-700">
                    {completedSteps}/{steps.length}
                  </div>
                  <div className="text-sm text-gray-600">{t("common.complete")}</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-green-700">{stepsWithData}</div>
                  <div className="text-sm text-gray-600">{t("footprints.filled")}</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-orange-700">{totalWarnings}</div>
                  <div className="text-sm text-gray-600">{t("footprints.revisionSubmission.attention")}</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-purple-700">
                    {Math.round((completedSteps / steps.length) * 100)}%
                  </div>
                  <div className="text-sm text-gray-600">{t("common.total")}</div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Main Review Content */}
          <div className="space-y-8">
            {/* Main Results - Featured prominently */}
            <div className="text-center space-y-6">
              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">{t("footprints.revisionSubmission.success")}</h3>
                <p className="text-gray-600">{t("footprints.revisionSubmission.dataSummary")}</p>
              </div>

              {/* Primary Results */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-2xl mx-auto">
                <div className="rounded-lg border border-teal-100 bg-white p-6">
                  <div className="flex items-center gap-2 text-teal-700 mb-1">
                    <BarChart4 className="h-5 w-5" />
                    <span className="text-lg font-medium">{t("common.totalCarbonFootprint")}</span>
                  </div>

                  <p className="text-xs italic text-teal-600 ml-7 mb-4">
                    {t("footprints.totalCarbonFootprintFormula")}
                  </p>

                  <div className="flex justify-end">
                    <span className="text-xl font-bold text-teal-700">
                      {formatNumber(totalCarbonFootprint)} <span>{t("dashboard.company.kgCO2")}</span>
                    </span>
                  </div>
                </div>

                <div className="rounded-lg border border-cyan-100 bg-white p-6">
                  <div className="flex items-center gap-2 text-cyan-600 mb-1">
                    <BarChart4 className="h-5 w-5" />
                    <span className="text-lg font-medium">{t("common.totalHydricFootprint")}</span>
                  </div>

                  <p className="text-xs italic text-cyan-600 ml-7 mb-4">
                    {t("footprints.totalHydricFootprintFormula")}
                  </p>

                  <div className="flex justify-end">
                    <span className="text-xl font-bold text-cyan-600">
                      {formatNumber(totalHydricFootprint)}{" "}
                      <span>
                        m<sup>3</sup>
                      </span>
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Summary Overview */}
            <div className="bg-gray-50 rounded-xl p-6">
              <h4 className="text-lg font-semibold text-gray-900 mb-4">
                {t("footprints.revisionSubmission.dataSummary")}
              </h4>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                {/* Data counters */}
                {(() => {
                  const materials = getConsumptionData("materials");
                  const electricity = getConsumptionData("electricity");
                  const fuels = getConsumptionData("fuels");
                  const water = getConsumptionData("water");
                  
                  return (
                    <>
                      {materials?.entries?.length > 0 && (
                        <div className="text-center py-3">
                          <div className="text-2xl font-bold text-purple-600 mb-1">{materials.entries.length}</div>
                          <div className="text-sm text-gray-600">{t("footprints.revisionSubmission.materials")}</div>
                        </div>
                      )}

                      {electricity?.entries?.length > 0 && (
                        <div className="text-center py-3">
                          <div className="text-2xl font-bold text-yellow-600 mb-1">{electricity.entries.length}</div>
                          <div className="text-sm text-gray-600">{t("footprints.revisionSubmission.energy")}</div>
                        </div>
                      )}

                      {fuels?.entries?.length > 0 && (
                        <div className="text-center py-3">
                          <div className="text-2xl font-bold text-orange-600 mb-1">{fuels.entries.length}</div>
                          <div className="text-sm text-gray-600">{t("footprints.revisionSubmission.fuels")}</div>
                        </div>
                      )}

                      {water?.waterEntries?.length > 0 && (
                        <div className="text-center py-3">
                          <div className="text-2xl font-bold text-blue-600 mb-1">{water.waterEntries.length}</div>
                          <div className="text-sm text-gray-600">{t("footprints.revisionSubmission.water")}</div>
                        </div>
                      )}
                    </>
                  );
                })()}
              </div>

              {/* General Data Summary */}
              {formData.generalData && (() => {
                const activityType = formData?.generalData?.activityType || company?.activityType;
                return (
                <div className="border-t border-gray-200 pt-4">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                    <div>
                      <div className="text-lg font-semibold text-gray-900">
                        {formatNumber(activityType === "extraction_and_transformation" 
                          ? Number(formData.generalData.prod_ext || 0) + Number(formData.generalData.prod_trsf || 0)
                          : activityType === "transformation" 
                            ? Number(formData.generalData.prod_trsf || 0) 
                            : Number(formData.generalData.prod_ext || 0)
                        )}t
                      </div>
                      <div className="text-xs text-gray-500">{t("footprints.revisionSubmission.production")}</div>
                    </div>
                    <div>
                      <div className="text-lg font-semibold text-gray-900">
                        {formatNumber(activityType === "extraction_and_transformation" 
                          ? Number(formData.generalData.fat_ext || 0) + Number(formData.generalData.fat_trsf || 0)
                          : activityType === "transformation" 
                            ? Number(formData.generalData.fat_trsf || 0) 
                            : Number(formData.generalData.fat_ext || 0)
                        )}€
                      </div>
                      <div className="text-xs text-gray-500">{t("footprints.revisionSubmission.revenue")}</div>
                    </div>
                    <div>
                      <div className="text-lg font-semibold text-gray-900">{year}</div>
                      <div className="text-xs text-gray-500">{t("footprints.revisionSubmission.year")}</div>
                    </div>
                    <div>
                      <div className="text-lg font-semibold text-gray-900">
                        {formData.products?.entries?.length || 0}
                      </div>
                      <div className="text-xs text-gray-500">{t("footprints.revisionSubmission.products")}</div>
                    </div>
                  </div>
                </div>
                );
              })()}
            </div>

            {/* Products Preview (if available) */}
            {formData.products?.entries?.length > 0 && (
              <div className="space-y-4">
                <h4 className="text-lg font-semibold text-gray-900">
                  {t("footprints.revisionSubmission.calculatedProducts")}
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {formData.products.entries.slice(0, 4).map((product: any, idx: number) => (
                    <div
                      key={idx}
                      className="bg-white rounded-lg border border-gray-200 shadow-sm hover:shadow transition-shadow"
                    >
                      <div className="p-4 border-b border-gray-200">
                        <div className="font-medium text-gray-900 mb-1">{product.name}</div>
                        <div className="text-sm text-gray-500">{product.productType}</div>
                      </div>

                      <div className="bg-teal-50/80 border-b py-3 px-4">
                        <div className="flex justify-between items-center text-teal-700 w-full">
                          <div className="flex items-center gap-1.5">
                            <BarChart4 className="h-4 w-4 text-teal-600" />
                            <span className="align-middle text-sm font-medium">{t("common.carbonFootprint")}</span>
                          </div>
                          <span className="text-sm font-bold text-teal-700">
                            {formatNumber((product.carbonFootprintTotal || 0) * (product.annualProduction || 0))} kg
                            CO₂e
                          </span>
                        </div>
                      </div>

                      <div className="bg-cyan-50/50 py-3 px-4">
                        <div className="flex justify-between items-center text-cyan-600 w-full">
                          <div className="flex items-center gap-1.5">
                            <BarChart4 className="h-4 w-4 text-cyan-600" />
                            <span className="align-middle text-sm font-medium">{t("common.hydricFootprint")}</span>
                          </div>
                          <span className="text-sm font-bold text-cyan-700">
                            {formatNumber((product.hydricFootprintTotal || 0) * (product.annualProduction || 0))} m³
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                {formData.products.entries.length > 4 && (
                  <div className="text-center text-sm text-gray-500">
                    {t("footprints.revisionSubmission.additionalProducts", {
                      count: formData.products.entries.length - 4,
                    })}
                  </div>
                )}
              </div>
            )}

            {/* Completion Status & Warnings */}
            <div className="space-y-4">
              {/* Progress Status */}
              <div className="bg-white rounded-lg border border-gray-200 p-4">
                <h4 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-teal-600" />
                  {t("footprints.revisionSubmission.dataSummary")}
                </h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {steps.map((step, index) => (
                    <div key={index} className="flex items-center gap-2">
                      {step.isComplete ? (
                        <CheckCircle className="w-4 h-4 text-green-500" />
                      ) : step.hasData ? (
                        <AlertCircle className="w-4 h-4 text-yellow-500" />
                      ) : (
                        <XCircle className="w-4 h-4 text-gray-400" />
                      )}
                      <span
                        className={`text-xs ${
                          step.isComplete
                            ? "text-green-700 font-medium"
                            : step.hasData
                            ? "text-yellow-700"
                            : "text-gray-500"
                        }`}
                      >
                        {step.name}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Save Draft Button */}
          {hasUnsavedChanges && onSaveDraft && (
            <div className="pt-4 border-t border-gray-200 mt-4">
              <div className="flex items-center justify-between p-4 bg-gray-50 border border-gray-200 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <AlertCircle className="h-4 w-4" />
                    <span>{t("footprints.consumption.unsavedChanges")}</span>
                  </div>
                  {lastSaved && (
                    <div className="text-xs text-gray-500">
                      {t("footprints.consumption.lastSaved")}:{" "}
                      {(() => {
                        // Convert Firestore timestamp to Date if needed
                        let date = lastSaved;
                        if (lastSaved && typeof lastSaved === "object" && "seconds" in lastSaved) {
                          date = new Date((lastSaved as { seconds: number }).seconds * 1000);
                        }
                        return date instanceof Date
                          ? date.toLocaleString("pt-PT")
                          : t("footprints.consumption.invalidDate");
                      })()}
                    </div>
                  )}
                </div>
                <Button
                  onClick={onSaveDraft}
                  disabled={isSavingDraft}
                  variant="outline"
                  size="sm"
                  className="border-teal-200 text-teal-600 hover:bg-teal-50"
                >
                  {isSavingDraft ? (
                    <>
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                      {t("footprints.consumption.saving")}
                    </>
                  ) : (
                    <>
                      <Check className="h-4 w-4 mr-2" />
                      {t("footprints.consumption.saveDraft")}
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}

          {/* Submit Button */}
          <div className="flex flex-col gap-3 pt-4 border-t border-gray-200 mt-4">
            {/* Success Display */}
            {submitted && (
              <Alert className="border-green-200 bg-green-50 flex items-center gap-2">
                <CheckCircle className="h-4 w-4 text-green-600" />
                <AlertDescription className="text-green-800">
                  <strong>{t("footprints.revisionSubmission.success")}</strong>{" "}
                  {t("footprints.revisionSubmission.successMessage", { year })}
                </AlertDescription>
              </Alert>
            )}

            {/* Error Display */}
            {submitError && (
              <Alert className="border-red-200 bg-red-50">
                <AlertTriangle className="h-4 w-4 text-red-600" />
                <AlertDescription className="text-red-800">
                  <strong>{t("footprints.revisionSubmission.error")}:</strong> {submitError}
                </AlertDescription>
              </Alert>
            )}

            <div className="flex justify-end">
              <Button
                onClick={handleSubmit}
                disabled={submitted || isSubmitting || totalWarnings > 0 || (originalStatus === "submitted" && !hasUnsavedChanges)}
                className="bg-teal-600 hover:bg-teal-700 text-white disabled:opacity-50"
                size="lg"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    {t("footprints.revisionSubmission.submitting")}
                  </>
                ) : submitted || (originalStatus === "submitted" && !hasUnsavedChanges) ? (
                  <>
                    <CheckCircle className="w-5 h-5 mr-2" />
                    {t("footprints.revisionSubmission.submittedSuccessfully")}
                  </>
                ) : (
                  <>
                    <ArrowUpToLine className="w-5 h-5 mr-2" />
                    {t("footprints.revisionSubmission.submitCalculation")}
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
